import { Fragment, useState } from "react";

import { generatePath } from "react-router-dom";

import {
  deleteOrganization,
  getCurrentOrganizationUsage,
} from "@/api/endpoints/organization";
import { getOngoingSearches } from "@/api/endpoints/searches";
import { DATE_FORMAT, PLAN_TYPE } from "@/constants";
import ConfirmModal from "@/containers/ConfirmModal";
import { useMessages } from "@/containers/Messages/MessageContext";
import PlanModal from "@/containers/PlanModal";
import { useI18n } from "@/contexts/i18nContext";
import { FEATURE } from "@/features";
import withRestriction from "@/hocs/withRestriction";
import { useHasFeature } from "@/hooks/useHasFeature";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { PUBLIC_SITE, ROUTES } from "@/routes_new";
import { stringifyQuery } from "@/utils";
import OrganizationName from "@/views/settings/account/partials/OrganizationName";
import { ACCESS_LEVELS } from "@components/AccessControl";
import AllEarsTitle from "@components/AllEarsTitle";
import Button from "@components/Button";
import { LanguageDisplay, SourceDisplay } from "@components/DataDisplay";
import Rhythm from "@components/Rhythm";
import Table from "@components/Table";
import TextButton from "@components/TextButton";
import Typography from "@components/Typography";
import { useQuery } from "@tanstack/react-query";

import Divider from "../partials/Divider";
import UsageConsumption from "./partials/UsageConsumption";

import "react-circular-progressbar/dist/styles.css";
import styles from "./AccountView.module.css";

const SearchesBlock = () => {
  const { data: orgData } = useOrganization();
  const { name } = orgData?.ok ? orgData.data : {};
  const { isLoading, data } = useQuery({
    queryKey: [name + "-searches", getOngoingSearches],
    queryFn: getOngoingSearches,
  });

  if (isLoading) return null;

  return (
    <Table tight data-testid={!isLoading && "active-alerts-list"}>
      <thead>
        <tr>
          <th>{L.ACCOUNT_MENTIONS_TABLE_TITLE}</th>
          <th>{L.ACCOUNT_MENTIONS_TABLE_MENTIONS}</th>
        </tr>
      </thead>

      <tbody>
        {data.data
          .filter((a) => a.kind === "report")
          .map((alert) => (
            <tr key={alert.uuid}>
              <td>
                <a
                  href={generatePath(ROUTES.MENTION_DETAIL, {
                    uuid: alert.uuid,
                  })}
                >
                  {alert.title}
                </a>
              </td>
              <td>{alert.num_mentions_for_current_month}</td>
            </tr>
          ))}
      </tbody>
    </Table>
  );
};

const CHANGE_NAME = false;
const DELETE_ORG = false;

const AccountInfo = () => {
  const { data: userData } = useUser();
  const { email } = userData?.ok ? userData.data : {};
  const { data: orgData } = useOrganization();
  const { name, plan, plan_subscription } = orgData?.ok ? orgData.data : {};
  const { isLoading, data } = useQuery({
    queryKey: [name + "-usage"],
    queryFn: getCurrentOrganizationUsage,
  });
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const canManageAccount = useHasFeature(FEATURE.CAN_MANAGE_ACCOUNT);
  const { formatDate } = useI18n();

  if (isLoading) return null;

  const accountInfo = data.data;

  const PLAN_LABEL = {
    [PLAN_TYPE.FREE]: "Free",
    [PLAN_TYPE.BASIC]: "Basic",
    [PLAN_TYPE.CUSTOM]: "Enterprise",
  };

  const RENEWAL_FREQUENCY_LABEL = {
    month: L.ACCOUNT_VIEW_ACCOUNT_PLAN_RENEWAL_MONTHLY,
    year: L.ACCOUNT_VIEW_ACCOUNT_PLAN_RENEWAL_YEARLY,
    custom: L.ACCOUNT_VIEW_ACCOUNT_PLAN_RENEWAL_CUSTOM,
  };

  const billingPortalLink = `${ROUTES.BILLING_PORTAL}?${stringifyQuery({
    prefilled_email: email,
  })}`;

  return (
    <>
      <Rhythm>
        <Typography
          data-testid="organization-account-header"
          variant="subtitle-2"
        >
          {L.ACCOUNT_VIEW_ACCOUNT_HEADER}
        </Typography>

        <Table tight>
          <tbody>
            {plan?.type && (
              <tr data-testid="plan-info">
                <td scope="row">{L.ACCOUNT_VIEW_ACCOUNT_PLAN}</td>
                <td>
                  {PLAN_LABEL[plan.type]}
                  {canManageAccount &&
                    [PLAN_TYPE.FREE, PLAN_TYPE.BASIC].includes(plan.type) && (
                      <TextButton
                        href={billingPortalLink}
                        onClick={
                          plan.type === PLAN_TYPE.FREE
                            ? (e) => {
                                e.preventDefault();
                                setPlansModalOpen(true);
                              }
                            : null
                        }
                        target="_blank"
                        data-testid="billing-portal-link"
                        rel="noreferrer"
                        className="ml-5"
                      >
                        {L.ACCOUNT_VIEW_ACCOUNT_PLAN_CHANGE_LINK}
                      </TextButton>
                    )}
                </td>
              </tr>
            )}

            {plan_subscription?.renewal_frequency && (
              <tr data-testid="plan-subscription-renewal-frequency">
                <td scope="row">
                  {L.ACCOUNT_VIEW_ACCOUNT_PLAN_RENEWAL_FREQUENCY}
                </td>
                <td>
                  {RENEWAL_FREQUENCY_LABEL[plan_subscription.renewal_frequency]}
                </td>
              </tr>
            )}

            {plan_subscription?.end_date && (
              <tr data-testid="plan-subscription-expires_at">
                <td scope="row">{L.ACCOUNT_VIEW_ACCOUNT_PLAN_EXPIRES_AT}</td>
                <td>
                  {formatDate(plan_subscription.end_date, DATE_FORMAT.LONG)}
                </td>
              </tr>
            )}

            {accountInfo.available_sources && (
              <tr>
                <td scope="row">{L.ACCOUNT_VIEW_ACCOUNT_AVAILABLE_SOURCES}</td>
                <td>
                  {accountInfo.available_sources.sort().map((source, i) => (
                    <Fragment key={source}>
                      {i !== 0 ? ", " : ""}
                      <SourceDisplay id={source} />
                    </Fragment>
                  ))}
                </td>
              </tr>
            )}

            {accountInfo.available_languages && (
              <tr>
                <td scope="row">
                  {L.ACCOUNT_VIEW_ACCOUNT_AVAILABLE_LANGUAGES}
                </td>
                <td data-testid="available-languages">
                  {accountInfo.available_languages.sort().map((lang, i) => (
                    <Fragment key={lang}>
                      {i !== 0 ? ", " : ""}
                      <LanguageDisplay id={lang} />
                    </Fragment>
                  ))}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Rhythm>

      <Divider />
      <Rhythm>
        <Typography variant="subtitle-2">
          {L.ACCOUNT_VIEW_USAGE_HEADER}
        </Typography>

        <Typography>{L.ACCOUNT_VIEW_USAGE_INTRO}</Typography>

        <div className={styles.grid}>
          <UsageConsumption
            label={L.ACCOUNT_VIEW_USAGE_MENTIONS_LABEL_WITH_PERIOD}
            showPercentage
            value={accountInfo.num_mentions_for_current_month}
          />

          {accountInfo.has_social_media_sources && (
            <UsageConsumption
              detailedText={
                accountInfo.max_num_social_media_mentions
                  ? `${accountInfo.num_social_media_mentions_for_current_month} ${L.ACCOUNT_VIEW_USAGE_MENTIONS_DETAILED_OF} ${accountInfo.max_num_social_media_mentions} ${L.ACCOUNT_VIEW_USAGE_MENTIONS_DETAILED_THIS_MONTH}`
                  : ""
              }
              label={L.ACCOUNT_VIEW_USAGE_MENTIONS_SOCIAL_MEDIA_LABEL}
              maxValue={accountInfo.max_num_social_media_mentions}
              showPercentage
              value={accountInfo.num_social_media_mentions_for_current_month}
            />
          )}

          <UsageConsumption
            label={L.ACCOUNT_VIEW_USAGE_ALERTS}
            maxValue={accountInfo.max_num_alerts}
            value={accountInfo.num_active_alerts}
          />
        </div>
      </Rhythm>
      <PlanModal
        show={plansModalOpen}
        onHide={() => setPlansModalOpen(false)}
      />
    </>
  );
};

const AccountView = () => {
  const { data: orgData } = useOrganization();
  const { uuid } = orgData?.ok ? orgData.data : {};
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { danger } = useMessages();
  const canEdit = useHasFeature(FEATURE.CAN_MANAGE_ACCOUNT);

  return (
    <>
      <AllEarsTitle title={L.NAV_ACCOUNT_SETTINGS} />
      {CHANGE_NAME && (
        <>
          <Rhythm>
            <Typography variant="subtitle-2">
              {L.ACCOUNT_VIEW_ACCOUNT_SETTINGS_TITLE}
            </Typography>
            <OrganizationName />
          </Rhythm>
          <Divider />
        </>
      )}
      <AccountInfo />
      <Divider />
      <Rhythm>
        <Typography variant="subtitle-2">
          {L.ACCOUNT_MENTIONS_HEADER}
        </Typography>
        <Typography>{L.ACCOUNT_MENTIONS_INTRO}</Typography>
        <SearchesBlock />
      </Rhythm>
      {DELETE_ORG && canEdit && (
        <>
          <Divider />
          <Rhythm>
            <Typography variant="subtitle-2">
              {L.ACCOUNT_VIEW_ACCOUNT_DELETE_TITLE}
            </Typography>
            <Typography>
              <strong>{L.ACCOUNT_VIEW_ACCOUNT_DELETE_PLAN_WARNING}</strong>
            </Typography>
            <Typography>{L.ACCOUNT_VIEW_ACCOUNT_DELETE_INFO}</Typography>
            <Button
              data-testid="delete-account-button"
              className={styles.remove}
              onClick={() => setDeleteModalOpen(true)}
            >
              {L.ACCOUNT_VIEW_ACCOUNT_DELETE_CONFIRM}
            </Button>
            <ConfirmModal
              show={deleteModalOpen}
              message={L.ACCOUNT_VIEW_ACCOUNT_DELETE_INFO}
              confirmText={L.ACCOUNT_VIEW_ACCOUNT_DELETE_CONFIRM}
              cancelText={L.ACCOUNT_VIEW_ACCOUNT_DELETE_CANCEL}
              title={L.ACCOUNT_VIEW_ACCOUNT_DELETE_ARE_YOU_SURE}
              onCancel={() => setDeleteModalOpen(false)}
              onConfirm={() => {
                setDeleteModalOpen(false);
                deleteOrganization(uuid)
                  .then((res) => {
                    if (res.ok) {
                      window.open(PUBLIC_SITE, "_self");
                    }
                  })
                  .catch(() => {
                    danger({
                      title: L.ACCOUNT_VIEW_ACCOUNT_DELETE_ERROR_TITLE,
                      message: L.ACCOUNT_VIEW_ACCOUNT_DELETE_ERROR,
                    });
                  });
              }}
            />
          </Rhythm>
          <Divider />
        </>
      )}
    </>
  );
};

export default withRestriction(AccountView, { level: ACCESS_LEVELS.ADMIN });
